import React from "react";
import { ownerApi } from "../../../../api/owner";
import { ownerFinanceStatsApi } from "../../../../api/ownerFinanceStats";
import { createOwnerJarvisCapabilities } from "./ownerJarvisCapabilities";
import { createOwnerJarvisCapabilityRegistry } from "./ownerJarvisCapabilityRegistry";
import { createOwnerJarvisSnapshotService } from "./ownerJarvisSnapshotService";
import { createOwnerJarvisMutationAwareApi } from "./ownerJarvisApi";
import { createOwnerJarvisBriefing } from "./ownerJarvisUtils";
import { executeOwnerJarvisUi } from "./ownerJarvisUiExecutor";
import { ownerJarvisConversationContext } from "./ownerJarvisConversationJournal";
import { persistOwnerJarvisTurn, readOwnerJarvisStoredTurn } from "./ownerJarvisStorage";
import { isExplicitOwnerJarvisBacklogCaptureRequest } from "./capabilities/ownerJarvisImprovementCapabilities";

function isProjectPaymentSession(session) {
  const kind = String(session?.kind || "");
  const stage = String(session?.stage || "");
  return kind === "project_payment_capture" || (kind === "project_review" && stage.startsWith("settlement_new_"));
}

export function useOwnerJarvisRuntime({ navigate }) {
  const financeProjectFlowRef = React.useRef(null);
  const dayFlowRef = React.useRef(null);
  const snapshotService = React.useMemo(() => createOwnerJarvisSnapshotService({ ownerApi }), []);

  React.useEffect(() => {
    snapshotService.start();
    snapshotService.refresh().catch(() => undefined);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") snapshotService.refresh().catch(() => undefined);
    }, snapshotService.ttlMs);
    return () => {
      window.clearInterval(timer);
      snapshotService.destroy();
    };
  }, [snapshotService]);

  const jarvisOwnerApi = React.useMemo(() => createOwnerJarvisMutationAwareApi({ ownerApi, snapshotService }), [snapshotService]);

  const capabilityRegistry = React.useMemo(() => createOwnerJarvisCapabilityRegistry(createOwnerJarvisCapabilities({
    ownerApi: jarvisOwnerApi,
    ownerFinanceStatsApi,
    navigate,
    flowRef: financeProjectFlowRef,
    dayFlowRef,
    snapshotService,
  }), { snapshotService }), [jarvisOwnerApi, navigate, snapshotService]);

  const actions = React.useMemo(() => capabilityRegistry.voiceActions(), [capabilityRegistry]);
  const introMessage = React.useMemo(() => createOwnerJarvisBriefing(), []);
  const refreshOwnerAfterTurn = React.useCallback(async (turn) => {
    if (!turn?.refreshOwner) return;
    const scopes = ["today", "projects", "finance"];
    snapshotService.invalidate(scopes);
    try { await snapshotService.refresh(); } catch { /* refresh remains stale and retries through the existing snapshot service */ }
    try { window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", { detail: { scopes } })); } catch { /* optional */ }
  }, [snapshotService]);
  const onUnhandledUtterance = React.useCallback(async (input) => {
    const raw = String(input?.raw || input?.text || "").trim();
    if (!raw) return { handled: false };

    // Captura explícita de producto/error tiene prioridad incluso dentro de un workflow.
    // El capability clásico es el que realmente persiste OwnerJarvisBacklogItem; no
    // debe quedar detrás del Director, porque un fallback conversacional "handled"
    // impediría que la captura ocurra. La aclaración de destino, si existe, sí vuelve
    // al turno determinístico del backend.
    const activeTurn = readOwnerJarvisStoredTurn();
    const targetClarificationActive = Boolean(activeTurn?.session?.backlogClarification?.originalMessage);
    const projectPaymentSessionActive = isProjectPaymentSession(activeTurn?.session);
    const explicitBacklogCapture = isExplicitOwnerJarvisBacklogCaptureRequest(raw);
    if (explicitBacklogCapture && !targetClarificationActive) {
      const captured = await capabilityRegistry.dispatch(input);
      if (captured?.handled) return captured;
    }
    if (projectPaymentSessionActive) {
      try {
        const response = await ownerApi.jarvisTurn({
          message: raw,
          session: activeTurn?.session || null,
          inputSource: input?.inputSource || "voice",
          currentPath: `${window.location.pathname || ""}${window.location.search || ""}`,
          conversationContext: ownerJarvisConversationContext(),
        });
        const turn = response?.turn || null;
        if (turn?.handled) {
          persistOwnerJarvisTurn(turn?.session ? turn : null);
          await refreshOwnerAfterTurn(turn);
          if (turn.ui) executeOwnerJarvisUi(navigate, turn.ui);
          return { handled: true, message: [turn.reply, turn.question].filter(Boolean).join(" "), highlightSection: turn.highlightSection || "today-jarvis", ai: turn.ai || null };
        }
      } catch {
        return { handled: false };
      }
    }
    if (targetClarificationActive) {
      try {
        const response = await ownerApi.jarvisTurn({
          message: raw,
          session: activeTurn?.session || null,
          inputSource: input?.inputSource || "voice",
          currentPath: `${window.location.pathname || ""}${window.location.search || ""}`,
          conversationContext: ownerJarvisConversationContext(),
        });
        const turn = response?.turn || null;
        if (turn?.handled) {
          if (turn.ui) executeOwnerJarvisUi(navigate, turn.ui);
          try { window.dispatchEvent(new CustomEvent("owner:jarvis-ai-interpreted", { detail: { turn } })); } catch { /* optional */ }
          return {
            handled: true,
            message: [turn.reply, turn.question].filter(Boolean).join(" "),
            highlightSection: turn.highlightSection || "today-jarvis",
            ai: turn.ai || null,
          };
        }
      } catch {
        return { handled: false };
      }
      return { handled: false };
    }

    // Una confirmación sensible o un wizard clásico ya abierto conserva prioridad:
    // no mandamos un “sí” aislado al Director sin el contexto de la operación local.
    if (capabilityRegistry.hasPendingInteraction?.()) {
      const pending = await capabilityRegistry.dispatch(input);
      if (pending?.handled) return pending;
    }

    // Para conversación/navegación libre el Director del backend va PRIMERO.
    // Antes hacíamos dispatch() clásico primero y capacidades amplias como
    // project-control secuestraban frases naturales (“quiero ver los pendientes
    // de los proyectos”) antes de que llegaran al Jarvis nuevo.
    try {
      const response = await ownerApi.jarvisInterpret({
        message: raw,
        session: activeTurn?.session || null,
        inputSource: input?.inputSource || "voice",
        currentPath: `${window.location.pathname || ""}${window.location.search || ""}`,
        conversationContext: ownerJarvisConversationContext(),
        runtimeContext: {
          priorityConversation: Boolean(hasPriorityConversation?.()),
          sessionKind: financeProjectFlowRef.current?.stage ? "finance_project_flow" : (dayFlowRef.current?.kind || ""),
          stage: financeProjectFlowRef.current?.stage || dayFlowRef.current?.stage || "",
        },
      });
      const turn = response?.turn || null;
      if (turn?.handled) {
        if (isProjectPaymentSession(turn?.session)) persistOwnerJarvisTurn(turn);
        await refreshOwnerAfterTurn(turn);
        if (turn.ui) executeOwnerJarvisUi(navigate, turn.ui);
        try { window.dispatchEvent(new CustomEvent("owner:jarvis-ai-interpreted", { detail: { turn } })); } catch { /* optional */ }
        return {
          handled: true,
          message: [turn.reply, turn.question].filter(Boolean).join(" "),
          highlightSection: turn.highlightSection || "today-jarvis",
          ai: turn.ai || null,
        };
      }
    } catch {
      // El runtime clásico queda como degradación segura si Director/API no responde.
    }

    const classic = await capabilityRegistry.dispatch(input);
    if (classic?.handled) return classic;
    return { handled: false };
  }, [capabilityRegistry, navigate, refreshOwnerAfterTurn]);
  const hasPriorityConversation = React.useCallback(() => (
    Boolean(financeProjectFlowRef.current?.stage)
    || Boolean(dayFlowRef.current?.kind)
    || Boolean(capabilityRegistry.hasPendingInteraction?.())
  ), [capabilityRegistry]);

  const resetConversationState = React.useCallback(() => {
    financeProjectFlowRef.current = null;
    dayFlowRef.current = null;
    try { window.sessionStorage.removeItem("wp-owner-day-pending-selection-until"); } catch { /* optional */ }
    capabilityRegistry.resetPendingInteractions?.();
  }, [capabilityRegistry]);

  return {
    actions,
    introMessage,
    onUnhandledUtterance,
    hasPriorityConversation,
    resetConversationState,
    capabilities: capabilityRegistry.describe(),
    loadNavigationLearning: ownerApi.navigationLearning,
    recordNavigationLearning: ownerApi.recordNavigationLearning,
  };
}
